/**
 * Persistence layer for OAuth connection records.
 *
 * All tokens are encrypted before being handed off to IndexedDB, using the
 * buyer's per-buyer Data Encryption Key (DEK). The DEK is fetched from the
 * license worker after auth and cached in sessionStorage — see lib/dek.ts.
 * Buyer A's blobs are encrypted with a key buyer B never sees.
 */

import { decryptJson, encryptJson } from "@agencylens/crypto";
import type { ConnectionRecord, ConnectionPlatform } from "@agencylens/shared";
import { idbGet, idbPut, idbDelete, idbList } from "./idb.js";
import { getDek } from "./dek.js";

interface GoogleTokenPayload {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

function connectionKey(platform: ConnectionPlatform, handle: string): string {
  return `connections:${platform}:${handle}`;
}

export async function saveGoogleConnection(
  platform: ConnectionPlatform,
  handle: string,
  tokens: GoogleTokenPayload,
): Promise<ConnectionRecord> {
  const now = new Date();
  const dek = await getDek();
  const expiresAt = new Date(now.getTime() + tokens.expires_in * 1000);
  const existing = await loadConnectionRaw(platform, handle);
  const accessBlob = await encryptJson(
    { access_token: tokens.access_token },
    dek,
  );
  // Google only returns refresh_token on the first consent (or when prompt=consent forces it).
  // Preserve any existing refresh token if Google didn't send one this round.
  const refreshToken = tokens.refresh_token
    ? await encryptJson({ refresh_token: tokens.refresh_token }, dek)
    : existing?.refreshToken;
  const record: ConnectionRecord = {
    platform,
    handle,
    scope: tokens.scope,
    expiresAt: expiresAt.toISOString(),
    accessToken: accessBlob,
    ...(refreshToken ? { refreshToken } : {}),
    createdAt: existing?.createdAt ?? now.toISOString(),
    updatedAt: now.toISOString(),
  };
  await idbPut(connectionKey(platform, handle), record);
  return record;
}

export async function loadConnectionRaw(
  platform: ConnectionPlatform,
  handle: string,
): Promise<ConnectionRecord | undefined> {
  return idbGet<ConnectionRecord>(connectionKey(platform, handle));
}

export async function readAccessToken(
  record: ConnectionRecord,
): Promise<string> {
  const dek = await getDek();
  const decoded = await decryptJson<{ access_token: string }>(
    record.accessToken,
    dek,
  );
  return decoded.access_token;
}

export async function readRefreshToken(
  record: ConnectionRecord,
): Promise<string | undefined> {
  if (!record.refreshToken) return undefined;
  const dek = await getDek();
  const decoded = await decryptJson<{ refresh_token: string }>(
    record.refreshToken,
    dek,
  );
  return decoded.refresh_token;
}

export async function deleteConnection(
  platform: ConnectionPlatform,
  handle: string,
): Promise<void> {
  await idbDelete(connectionKey(platform, handle));
}

interface MetaTokenPayload {
  access_token: string;
  token_type: string;
  /** Long-lived tokens are ~60 days. May be omitted on rare short-lived paths. */
  expires_in?: number;
}

/**
 * Persist a Meta OAuth token. Meta does not issue refresh tokens; the
 * long-lived token expires after ~60 days and the user must re-authenticate.
 */
export async function saveMetaConnection(
  handle: string,
  tokens: MetaTokenPayload,
): Promise<ConnectionRecord> {
  const now = new Date();
  const dek = await getDek();
  // Default to 60 days if Meta didn't return expires_in (covers some test paths)
  const ttl = tokens.expires_in ?? 60 * 24 * 60 * 60;
  const expiresAt = new Date(now.getTime() + ttl * 1000);
  const existing = await loadConnectionRaw("meta-ads", handle);
  const accessBlob = await encryptJson(
    { access_token: tokens.access_token },
    dek,
  );
  const record: ConnectionRecord = {
    platform: "meta-ads",
    handle,
    scope: "ads_read,ads_management,business_management",
    expiresAt: expiresAt.toISOString(),
    accessToken: accessBlob,
    createdAt: existing?.createdAt ?? now.toISOString(),
    updatedAt: now.toISOString(),
  };
  await idbPut(connectionKey("meta-ads", handle), record);
  return record;
}

export async function listConnectionKeys(): Promise<string[]> {
  return idbList("connections:");
}

export function isExpired(record: ConnectionRecord, skewSeconds = 60): boolean {
  const ts = new Date(record.expiresAt).getTime();
  return Date.now() + skewSeconds * 1000 >= ts;
}
